import React from 'react';
import { MatchState, MatchAction } from '../types';
import { ValidationError } from '../utils/validation';
import { PersistedMatchData } from '../hooks/useMatchPersistence';
import { SetupData } from '../hooks/useMatchData';
import {
  BeforeMatch,
  AfterMatch,
  DuringMatch,
  ErrorScreen,
  MatchSetupScreen,
  MatchRecoveryModal,
  PairedIdleScreen
} from './organisms';
import { PairedCourt, PairingLink } from '../hooks/useCourtPairing';
import { AppMode } from '../hooks/useAppConfig';
import { useTranslation } from '../i18n/hooks/useTranslation';
import { useLiveBroadcast } from '../hooks/useLiveBroadcast';

interface AppShellProps {
  // États de validation
  isValidating: boolean;
  hasErrors: boolean;
  validationErrors: ValidationError[];
  isInitialized: boolean;
  matchData: any;
  setupData: SetupData | null;
  dataSource: string;
  retry: () => void;
  
  // Navigation
  screenToShow: 'recovery_modal' | 'before_match' | 'setup_screen';
  
  // Récupération de match
  savedMatch: PersistedMatchData | null;
  handleRestoreMatch: () => void;
  handleStartNewMatch: () => void;
  
  // État du match
  matchState: MatchState;
  dispatch: React.Dispatch<MatchAction>;
  realStateInitialized: boolean;
  
  // Utilitaires
  clearData: () => void;
  isMatchActive: boolean;

  wakeLock: ReturnType<typeof import('../hooks/useWakeLock').useWakeLock>;

  // Court pairing (referee tablet)
  pairedCourt: PairedCourt | null;
  pairingLink: PairingLink;
  /** Absent when config.json does not allow manual matches on a paired tablet. */
  onManualMatch?: () => void;
  appMode: AppMode;
}

/**
 * Composant qui gère l'affichage conditionnel de tous les écrans de l'application
 * Code extrait EXACTEMENT d'App.tsx sans modification
 */
export function AppShell({
  isValidating,
  hasErrors,
  validationErrors,
  isInitialized,
  matchData,
  setupData,
  dataSource,
  retry,
  screenToShow,
  savedMatch,
  handleRestoreMatch,
  handleStartNewMatch,
  matchState,
  dispatch,
  realStateInitialized,
  clearData,
  isMatchActive,
  wakeLock,
  pairedCourt,
  pairingLink,
  onManualMatch,
  appMode
}: AppShellProps) {
  const { t } = useTranslation();

  // Live broadcast: hook lives here so it survives screen transitions
  // (DuringMatch unmounts when currentScreen changes to 'after')
  const { broadcastStatus } = useLiveBroadcast({
    matchState,
    enabled: Boolean(matchState.matchInfo.liveScoreUrl)
  });

  // Modal de récupération (code exact d'App.tsx)
  if (screenToShow === 'recovery_modal' && savedMatch) {
    return (
      <MatchRecoveryModal
        savedMatch={savedMatch}
        incomingMatch={isInitialized ? matchData : null}
        setupData={setupData}
        onRestore={handleRestoreMatch}
        onStartNew={handleStartNewMatch}
      />
    );
  }

  // Chargement initial (code exact d'App.tsx)
  if (isValidating) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-6 text-center max-w-md">
          <div className="animate-spin h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 mb-2">{t('screens.loading.validatingData')}</p>
          {dataSource && (
            <p className="text-xs text-gray-500">{t('screens.loading.source')}: {dataSource}</p>
          )}
        </div>
      </div>
    );
  }

  // Affichage des erreurs de validation (seulement si des données ont été fournies)
  // Si dataSource est vide, c'est qu'aucune donnée n'a été passée → setup_screen
  if (hasErrors && dataSource) {
    const enhancedErrors = [
      ...validationErrors,
      {
        field: t('screens.loading.dataSource'),
        message: dataSource
      }
    ];

    return <ErrorScreen errors={enhancedErrors} onRetry={retry} paired={appMode === 'paired'} />;
  }

  // Tablette pairée et inactive : elle attend le match envoyé sur son court
  if (screenToShow === 'setup_screen' && pairedCourt && !setupData) {
    return <PairedIdleScreen pairedCourt={pairedCourt} link={pairingLink} wakeLock={wakeLock} onManualMatch={onManualMatch} />;
  }

  // Afficher MatchSetupScreen selon la logique centralisée
  if (screenToShow === 'setup_screen') {
    return <MatchSetupScreen setupData={setupData} />;
  }

  // Données invalides ou manquantes (code exact d'App.tsx)
  if (!isInitialized || !matchData) {
    return <ErrorScreen 
      paired={appMode === 'paired'}
      errors={[
        { 
          field: 'Application', 
          message: t('screens.loading.dataNotAvailable')
        }
      ]} 
      onRetry={retry} 
    />;
  }

  // Attendre que le vrai state soit initialisé (code exact d'App.tsx)
  if (!realStateInitialized) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-6 text-center">
          <div className="animate-spin h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600 mb-2">{t('screens.loading.initializingMatch')}</p>
          <div className="text-xs text-gray-500 space-y-1">
            <div>{t('screens.loading.match')}: {matchData.competitionName}</div>
            <div>{t('screens.loading.players')}: {matchData.playerA} vs {matchData.playerB}</div>
          </div>
        </div>
      </div>
    );
  }

  // Rendu de l'écran approprié (code exact d'App.tsx)
  const renderCurrentScreen = () => {
    switch (matchState.currentScreen) {
      case 'before':
        return <BeforeMatch matchState={matchState} dispatch={dispatch} wakeLock={wakeLock} />;
      case 'during':
        return <DuringMatch matchState={matchState} dispatch={dispatch} wakeLock={wakeLock} broadcastStatus={broadcastStatus} />;
      case 'after':
        return <AfterMatch matchState={matchState} dispatch={dispatch} wakeLock={wakeLock} appMode={appMode} />;
      default:
        return <BeforeMatch matchState={matchState} dispatch={dispatch} wakeLock={wakeLock} />;
    }
  };

  // Rendu final avec debug info (code exact d'App.tsx)
  return (
    <div className="font-sans">
      {/* Debug info en développement */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed top-0 right-0 bg-black text-white text-xs p-2 z-50 opacity-75 pointer-events-none">
          {t('screens.loading.source')}: {dataSource}
          <button onClick={clearData} className="ml-2 bg-red-500 px-1 pointer-events-auto">
            {t('common.clear')}
          </button>
          {isMatchActive && (
            <div className="bg-orange-500 px-1 ml-1">
              {t('screens.loading.protected')}
            </div>
          )}
        </div>
      )}
      
      {renderCurrentScreen()}
    </div>
  );
}